/**
 * Communities, on the in-memory store.
 *
 * Its own file for the same reason channels has one: index.js is already
 * thirty kilobytes, and a service with rules worth reading is easier to
 * check in a file holding nothing else.
 *
 * The thing this mock has to get right is that a community's groups are
 * ORDINARY chats. They live in state.chats with a communityId, exactly as
 * they live in the chats table with a community_id — so joining one shows
 * it in the chat list for free, and a mock that kept them somewhere else
 * would hide the bug where that stops being true.
 */

import { state, subscribe, emit, latency } from './store.js'
import { toCommunity, toChat, toUser } from '../../models/index.js'
import { newId } from '../../utils/id.js'
import { ZivError, ErrorCode } from '../errors.js'

const me = () => state.currentUserId

/** From docs/references/19-communities.png: announcement plus a couple. */
const GROUPS_IN_PREVIEW = 3

/** The chats under a community, announcement first, with my participation. */
function groupsOf (communityId, limit = null) {
  const rows = [...state.chats.values()]
    .filter(c => c.communityId === communityId)
    .sort((a, b) => {
      if (Boolean(b.isAnnouncement) !== Boolean(a.isAnnouncement)) {
        return a.isAnnouncement ? -1 : 1
      }
      return (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0)
    })
    .map(chat => ({
      ...toChat(chat.id, chat),
      isJoined: (chat.participantIds ?? []).includes(me()),
      isAnnouncement: Boolean(chat.isAnnouncement)
    }))

  return limit ? rows.slice(0, limit) : rows
}

function view (record, { limit = null } = {}) {
  const membership = record.members.get(me())
  return toCommunity(record.id, {
    id:          record.id,
    name:        record.name,
    description: record.description,
    avatarUrl:   record.avatarUrl,
    announcementChatId: record.announcementChatId,
    isMember:    Boolean(membership),
    isAdmin:     membership === 'admin',
    memberCount: record.members.size,
    groupCount:  [...state.chats.values()].filter(c => c.communityId === record.id).length,
    groups:      groupsOf(record.id, limit),
    createdBy:   record.createdBy,
    createdAt:   record.createdAt
  })
}

function requireCommunity (communityId) {
  const record = state.communities.get(communityId)
  if (!record) throw new ZivError(ErrorCode.NOT_FOUND, 'Community not found.')
  return record
}

function requireMember (record) {
  if (!record.members.has(me())) {
    throw new ZivError(ErrorCode.PERMISSION, 'You are not in this community.')
  }
}

function requireAdmin (record) {
  if (record.members.get(me()) !== 'admin') {
    throw new ZivError(ErrorCode.PERMISSION, 'Only a community admin can do that.')
  }
}

/**
 * Refuse to leave a community without an admin.
 *
 * Nothing can repair that state: every administrative path needs an admin,
 * so a community that loses its last one is frozen for good.
 */
function assertNotLastAdmin (record, userId) {
  if (record.members.get(userId) !== 'admin') return
  const admins = [...record.members.values()].filter(r => r === 'admin').length
  if (admins <= 1) {
    throw new ZivError(
      ErrorCode.INVALID_INPUT,
      'Make someone else an admin first, or delete the community.'
    )
  }
}

/** Create a group chat in the store, shaped like every other one. */
function makeGroup ({ name, communityId, isAnnouncement = false, participantIds }) {
  const id = `c_${newId()}`
  state.chats.set(id, {
    id,
    type: 'group',
    name,
    description: null,
    avatarUrl: null,
    communityId,
    isAnnouncement,
    participantIds,
    participants: participantIds.map(userId => ({
      userId,
      role: userId === me() ? 'admin' : 'member',
      joinedAt: new Date()
    })),
    lastMessage: null,
    lastMessageAt: null,
    userState: {},
    createdBy: me(),
    createdAt: new Date()
  })
  state.messages.set(id, [])
  return id
}

export const communities = {
  subscribeToMine (callback) {
    const send = () => callback(
      [...state.communities.values()]
        .filter(r => r.members.has(me()))
        .sort((a, b) => b.createdAt - a.createdAt)
        .map(r => view(r, { limit: GROUPS_IN_PREVIEW }))
    )
    queueMicrotask(send)
    // 'chats' as well: a message in one of the groups changes the preview
    // shown on the community's row, and that change is emitted there.
    const offCommunities = subscribe('communities', send)
    const offChats = subscribe('chats', send)
    return () => { offCommunities(); offChats() }
  },

  subscribeToGroups (communityId, callback) {
    const send = () => callback(
      state.communities.has(communityId) ? groupsOf(communityId) : []
    )
    queueMicrotask(send)
    const offCommunities = subscribe('communities', send)
    const offChats = subscribe('chats', send)
    return () => { offCommunities(); offChats() }
  },

  async get (communityId) {
    await latency()
    return view(requireCommunity(communityId))
  },

  async create ({ name, description = null, avatarUrl = null, groupIds = [] }) {
    await latency()
    if (!String(name ?? '').trim()) {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'A community needs a name.')
    }

    for (const chatId of groupIds) {
      const chat = state.chats.get(chatId)
      if (!chat || chat.type !== 'group' || chat.communityId) {
        throw new ZivError(
          ErrorCode.PERMISSION,
          'You can only bring in groups you administer that are not already in a community.'
        )
      }
    }

    const id = `cu_${newId()}`
    // The announcement group exists from the first instant. A community
    // without one is not a state the rest of the code should have to
    // handle — making it representable means a null check on every screen.
    const announcementChatId = makeGroup({
      name: 'Announcements',
      communityId: id,
      isAnnouncement: true,
      participantIds: [me()]
    })

    state.communities.set(id, {
      id,
      name: String(name).trim(),
      description,
      avatarUrl,
      announcementChatId,
      members: new Map([[me(), 'admin']]),
      createdBy: me(),
      createdAt: new Date()
    })

    for (const chatId of groupIds) {
      state.chats.get(chatId).communityId = id
    }

    emit('communities')
    emit('chats')
    return view(state.communities.get(id))
  },

  async update (communityId, patch) {
    await latency()
    const record = requireCommunity(communityId)
    requireAdmin(record)
    if (patch.name !== undefined) record.name = String(patch.name).trim()
    if (patch.description !== undefined) record.description = patch.description ?? null
    if (patch.avatarUrl !== undefined) record.avatarUrl = patch.avatarUrl ?? null
    emit('communities')
    return view(record)
  },

  async addGroup (communityId, chatId) {
    await latency()
    const record = requireCommunity(communityId)
    requireAdmin(record)

    const chat = state.chats.get(chatId)
    if (!chat) throw new ZivError(ErrorCode.NOT_FOUND, 'Group not found.')
    if (chat.type !== 'group') {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'Only groups can be added to a community.')
    }
    if (chat.communityId === communityId) {
      throw new ZivError(ErrorCode.CONFLICT, 'That group is already in this community.')
    }
    if (chat.communityId) {
      throw new ZivError(ErrorCode.CONFLICT, 'That group is already in another community.')
    }

    chat.communityId = communityId
    emit('communities')
    emit('chats')
    return { ...toChat(chat.id, chat), isJoined: true, isAnnouncement: false }
  },

  async createGroup (communityId, name) {
    await latency()
    const record = requireCommunity(communityId)
    requireAdmin(record)
    if (!String(name ?? '').trim()) {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'A group needs a name.')
    }

    const chatId = makeGroup({
      name: String(name).trim(),
      communityId,
      participantIds: [me()]
    })

    emit('communities')
    emit('chats')
    const chat = state.chats.get(chatId)
    return { ...toChat(chatId, chat), isJoined: true, isAnnouncement: false }
  },

  async removeGroup (communityId, chatId) {
    await latency()
    const record = requireCommunity(communityId)
    requireAdmin(record)

    const chat = state.chats.get(chatId)
    if (!chat || chat.communityId !== communityId) {
      throw new ZivError(ErrorCode.NOT_FOUND, 'That group is not in this community.')
    }
    if (chat.isAnnouncement) {
      throw new ZivError(
        ErrorCode.INVALID_INPUT,
        'The announcement group cannot be removed from its community.'
      )
    }

    // Unparented, not deleted: the conversation survives as an ordinary
    // group, because it is people's messages.
    chat.communityId = null
    emit('communities')
    emit('chats')
  },

  async joinGroup (communityId, chatId) {
    await latency()
    const record = requireCommunity(communityId)
    requireMember(record)

    const chat = state.chats.get(chatId)
    if (!chat || chat.communityId !== communityId) {
      throw new ZivError(ErrorCode.NOT_FOUND, 'That group is not in this community.')
    }

    if (!chat.participantIds.includes(me())) {
      chat.participantIds.push(me())
      chat.participants.push({ userId: me(), role: 'member', joinedAt: new Date() })
    }

    emit('communities')
    emit('chats')
    return { ...toChat(chat.id, chat), isJoined: true, isAnnouncement: Boolean(chat.isAnnouncement) }
  },

  async members (communityId) {
    await latency()
    const record = requireCommunity(communityId)
    requireMember(record)

    return [...record.members.entries()].map(([userId, role]) => {
      const user = toUser(userId, state.users.get(userId) ?? {})
      return {
        ...user,
        userId,
        role,
        joinedAt: record.createdAt
      }
    }).sort((a, b) => (b.role === 'admin') - (a.role === 'admin'))
  },

  async addMembers (communityId, userIds = []) {
    await latency()
    const record = requireCommunity(communityId)
    requireAdmin(record)
    if (userIds.length === 0) {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'Nobody to add.')
    }

    const announcement = state.chats.get(record.announcementChatId)
    for (const userId of userIds) {
      if (!state.users.has(userId)) {
        throw new ZivError(ErrorCode.INVALID_INPUT, 'One of those accounts does not exist.')
      }
      if (!record.members.has(userId)) record.members.set(userId, 'member')

      // Joining the community means joining its announcement group. That
      // is the whole point of an announcement group — it reaches everyone.
      if (announcement && !announcement.participantIds.includes(userId)) {
        announcement.participantIds.push(userId)
        announcement.participants.push({ userId, role: 'member', joinedAt: new Date() })
      }
    }

    emit('communities')
    emit('chats')
  },

  async removeMember (communityId, userId) {
    await latency()
    const record = requireCommunity(communityId)
    requireAdmin(record)
    if (userId === me()) {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'Use leave to remove yourself.')
    }
    dropMember(record, userId)
    emit('communities')
    emit('chats')
  },

  async setRole (communityId, userId, role) {
    await latency()
    const record = requireCommunity(communityId)
    requireAdmin(record)
    if (role !== 'admin' && role !== 'member') {
      throw new ZivError(ErrorCode.INVALID_INPUT, 'Role must be admin or member.')
    }
    if (!record.members.has(userId)) {
      throw new ZivError(ErrorCode.NOT_FOUND, 'That person is not in this community.')
    }
    if (role === 'member') assertNotLastAdmin(record, userId)

    record.members.set(userId, role)
    emit('communities')
  },

  async leave (communityId) {
    await latency()
    const record = requireCommunity(communityId)
    requireMember(record)
    assertNotLastAdmin(record, me())
    dropMember(record, me())
    emit('communities')
    emit('chats')
  },

  async delete (communityId) {
    await latency()
    const record = requireCommunity(communityId)
    requireAdmin(record)

    // Every group except the announcement one survives, unparented.
    // Deleting a community must not destroy months of other people's
    // conversations.
    for (const chat of state.chats.values()) {
      if (chat.communityId !== communityId) continue
      if (chat.isAnnouncement) {
        state.chats.delete(chat.id)
        state.messages.delete(chat.id)
      } else {
        chat.communityId = null
      }
    }

    state.communities.delete(communityId)
    emit('communities')
    emit('chats')
  }
}

/**
 * Out of the community and its announcement group.
 *
 * Their other groups are left alone on purpose: those are conversations
 * they were in, and removal from a community is not a ban from every room
 * they ever joined.
 */
function dropMember (record, userId) {
  record.members.delete(userId)

  const announcement = state.chats.get(record.announcementChatId)
  if (!announcement) return
  announcement.participantIds = announcement.participantIds.filter(id => id !== userId)
  announcement.participants = announcement.participants.filter(p => p.userId !== userId)
}
